import React from 'react';
import { titleize, formatStatValue } from '../../lib/clubHelpers';

function playerChips(arr, nameMap, field) {
  if (!Array.isArray(arr) || !arr.length) return [];
  return arr.slice(0, 4).map((e) => ({
    name: nameMap?.[e?.player_id] || `#${e?.player_id ?? '?'}`,
    value: e?.[field] ?? e?.total ?? null,
  }));
}

export function classifyStat(code, v, nameMap) {
  const C = String(code || '').toLowerCase();
  if (v == null || v === '') return { type: 'scalar', value: '—' };
  if (typeof v !== 'object') return { type: 'scalar', value: String(v) };

  if (C === 'ball-possession') {
    const p = v.average ?? v.percentage ?? v.count;
    return { type: 'percent', value: p != null ? `${p}%` : '—', pct: p };
  }
  if (/highest.?rated/.test(C)) {
    const pid = v.player_id ?? v.id;
    const r = v.rating ?? v.average_rating ?? v.rating_avg;
    const nm = nameMap?.[pid] || (pid != null ? `Player #${pid}` : 'Player');
    return { type: 'players', items: [{ name: nm, value: r != null ? Number(r).toFixed(2) : null }] };
  }
  if (Array.isArray(v.most_appearing_players)) return { type: 'players', items: playerChips(v.most_appearing_players, nameMap, 'matches') };
  if (Array.isArray(v.most_substituted_players)) return { type: 'players', items: playerChips(v.most_substituted_players, nameMap, 'total') };
  if (Array.isArray(v.most_injured_players)) return { type: 'players', items: playerChips(v.most_injured_players, nameMap, 'total') };

  if (C === 'players-footing')
    return { type: 'chips', items: [
      { label: 'Right', value: v.right, tone: 'teal' },
      { label: 'Left', value: v.left, tone: 'gray' },
      { label: 'Both/Unknown', value: v.unknown, tone: 'gold' },
    ].filter((i) => i.value != null) };

  if (C === 'average-player-height') {
    const m = [['DEF', 'avg_defender_height'], ['MID', 'avg_midfielder_height'], ['GOA', 'avg_goalkeeper_height'], ['ATT', 'avg_attacker_height']];
    const items = m.filter(([, k]) => v[k] != null).map(([lab, k]) => ({ label: lab, value: v[k] }));
    return items.length ? { type: 'chips', items } : { type: 'scalar', value: '—' };
  }

  if ('scored' in v && 'missed' in v)
    return { type: 'metrics', items: [
      { label: 'Scored', value: v.scored, tone: 'teal' },
      { label: 'Missed', value: v.missed, tone: 'coral' },
      { label: 'Conv.', value: v.conversion_rate != null ? `${v.conversion_rate}%` : null },
    ].filter((i) => i.value != null) };

  if (C === 'shots') return { type: 'metrics', items: [
    { label: 'Total', value: v.total ?? v.count },
    { label: 'On target', value: v.on_target },
  ].filter((i) => i.value != null) };

  if ('fouls_per_card' in v && 'cards_per_foul' in v) return { type: 'metrics', items: [
    { label: 'Fouls/card', value: v.fouls_per_card },
    { label: 'Cards/foul', value: v.cards_per_foul },
  ].filter((i) => i.value != null) };

  if ('most_scored_half' in v) return { type: 'metrics', items: [
    { label: 'Half', value: v.most_scored_half },
    { label: 'Goals', value: v.most_scored_half_goals },
  ].filter((i) => i.value != null) };

  if ('most_frequent_scoring_minute' in v) return { type: 'metrics', items: [
    { label: 'Minute', value: v.most_frequent_scoring_minute != null ? `${v.most_frequent_scoring_minute}′` : null },
    { label: 'Goals', value: v.amount_of_goals },
  ].filter((i) => i.value != null) };

  if ('total_interceptions' in v && 'interceptions_per_game' in v) return { type: 'metrics', items: [
    { label: 'Total', value: v.total_interceptions },
    { label: 'Per game', value: v.interceptions_per_game },
  ].filter((i) => i.value != null) };

  if ('passes_per_game' in v) return { type: 'metrics', items: [
    { label: 'Total', value: v.total_passes },
    { label: 'Per game', value: v.passes_per_game },
    { label: 'Per goal', value: v.passes_per_goal },
  ].filter((i) => i.value != null) };

  if ('assists_per_game' in v) return { type: 'metrics', items: [
    { label: 'Total', value: v.total_assists },
    { label: 'Per game', value: v.assists_per_game },
    { label: 'Min/assist', value: v.minutes_per_assist },
  ].filter((i) => i.value != null) };

  if ('won_both_halves' in v && 'scored_both_halves' in v) return { type: 'metrics', items: [
    { label: 'Won both', value: v.won_both_halves },
    { label: 'Scored both', value: v.scored_both_halves },
    { label: 'Comebacks', value: v.comebacks },
  ].filter((i) => i.value != null) };

  if (C === 'number-of-goals') {
    const overs = [['0.5+', 'over_0_5'], ['1.5+', 'over_1_5'], ['2.5+', 'over_2_5'], ['3.5+', 'over_3_5']];
    const items = overs.map(([lab, k]) => ({ label: lab, pct: v[k]?.matches?.percentage, count: v[k]?.matches?.count })).filter((i) => i.pct != null || i.count != null);
    return items.length ? { type: 'over', items } : { type: 'scalar', value: '—' };
  }
  if (C === 'goal-results' || 'gf_over_0_5' in v) {
    const overs = [['0.5+', 'gf_over_0_5'], ['1.5+', 'gf_over_1_5'], ['2.5+', 'gf_over_2_5'], ['3.5+', 'gf_over_3_5']];
    const items = overs.map(([lab, k]) => ({ label: lab, count: v[k] })).filter((i) => i.count != null);
    return items.length ? { type: 'over', items } : { type: 'scalar', value: '—' };
  }
  if (C === 'scoring-minutes' || C === 'conceded-scoring-minutes') {
    const buckets = Object.keys(v).filter((k) => /-/.test(k) && typeof v[k] === 'object').sort();
    const items = buckets.map((b) => ({ label: b, value: v[b]?.count ?? 0 }));
    return items.length ? { type: 'bars', items } : { type: 'scalar', value: '—' };
  }
  if (C === 'team-wins' || C === 'team-draws' || C === 'team-lost') {
    const tone = C === 'team-wins' ? 'teal' : C === 'team-lost' ? 'coral' : 'gray';
    const count = v.count ?? v.all?.count;
    const pct = v.percentage ?? v.all?.percentage;
    return { type: 'countnote', value: count != null ? count : '—', note: pct != null ? `${pct}%` : null, tone };
  }
  if ('national_team_players' in v && Array.isArray(v.national_team_players)) {
    return { type: 'nations', count: v.national_team_players.length, items: v.national_team_players.slice(0, 8).map((e) => e?.team || '—') };
  }
  if (Object.keys(v).length === 1 && 'scoring_frequency' in v) return { type: 'note', text: `Every ${v.scoring_frequency} min` };
  if (Object.keys(v).length === 1 && 'total_minutes_played' in v) return { type: 'scalar', value: String(v.total_minutes_played) };

  if ('count' in v && 'percentage' in v) return { type: 'countnote', value: v.count, note: `${v.percentage}%` };
  if (v.all && typeof v.all === 'object' && 'count' in v.all)
    return { type: 'countnote', value: v.all.count, note: v.all.percentage != null ? `${v.all.percentage}%` : null };

  if ('count' in v) return { type: 'scalar', value: String(v.count) };
  if ('total' in v) return { type: 'scalar', value: String(v.total) };
  if ('average' in v) return { type: 'scalar', value: String(v.average) };
  if ('value' in v) return { type: 'scalar', value: String(v.value) };

  const allScalar = Object.keys(v).length && Object.values(v).every((x) => x == null || typeof x !== 'object');
  if (allScalar) {
    const entries = Object.entries(v).filter(([, val]) => val != null);
    if (entries.length === 1) return { type: 'scalar', value: String(entries[0][1]) };
    return { type: 'metrics', items: entries.slice(0, 4).map(([k, val]) => ({ label: titleize(k), value: val })) };
  }
  return { type: 'scalar', value: formatStatValue(code, v, nameMap) };
}

export function StatTile({ d, nameMap }) {
  const label = d?.type?.name || '—';
  const desc = classifyStat(d?.type?.code, d?.value, nameMap);
  const wide = desc.type === 'over' || desc.type === 'bars';
  return (
    <div className={'stat-tile' + (wide ? ' stat-tile-wide sm:col-span-2' : '')}>
      <div className="stat-tile-l">{label}</div>
      <TileBody desc={desc} />
    </div>
  );
}

function TileBody({ desc }) {
  switch (desc.type) {
    case 'scalar': return <div className="stat-tile-v">{desc.value}</div>;
    case 'note': return <div className="stat-tile-v">{desc.text}</div>;
    case 'percent':
      return (
        <>
          <div className="stat-tile-v">{desc.value}</div>
          {desc.pct != null && <div className="stat-bar"><i style={{ width: `${Math.min(100, Number(desc.pct) || 0)}%` }} /></div>}
        </>
      );
    case 'countnote':
      return (
        <>
          <div className="stat-tile-v">{desc.value}</div>
          {desc.note && <div className="stat-tile-note">{desc.note}</div>}
          {desc.note != null && <div className="stat-bar"><i style={{ width: `${Math.min(100, Number(String(desc.note).replace('%', '')) || 0)}%` }} /></div>}
        </>
      );
    case 'metrics':
      return (
        <div className="stat-tile-body stat-metrics">
          {desc.items.map((it, i) => (
            <div className="stat-metric" key={i}>
              <div className="stat-metric-l">{it.label}</div>
              <div className="stat-metric-v">{it.value ?? '—'}</div>
            </div>
          ))}
        </div>
      );
    case 'chips':
      return (
        <div className="stat-tile-body stat-chips">
          {desc.items.map((it, i) => (
            <span className={'stat-chip' + (it.tone ? ` t-tone-${it.tone}` : '')} key={i}>{it.label} {it.value != null && <b>{it.value}</b>}</span>
          ))}
        </div>
      );
    case 'players':
      return (
        <div className="stat-tile-body stat-players">
          {desc.items.map((it, i) => (
            <span className="stat-player" key={i}>{it.name}{it.value != null && <b>{it.value}</b>}</span>
          ))}
        </div>
      );
    case 'nations':
      return (
        <>
          <div className="stat-tile-v">{desc.count}{desc.count === 1 ? ' player' : ' players'}</div>
          <div className="stat-nations-l">
            {desc.items.map((n, i) => <span key={i}>{n}</span>)}
          </div>
        </>
      );
    case 'over': {
      const max = Math.max(1, ...desc.items.map((i) => Number(i.pct ?? i.count) || 0));
      return (
        <div className="stat-tile-body stat-over">
          {desc.items.map((it, i) => {
            const val = it.pct != null ? `${it.pct}%` : it.count;
            const w = it.pct != null ? Number(it.pct) || 0 : (Number(it.count) / max) * 100;
            return (
              <div className="stat-over-i" key={i}>
                <div className="stat-over-l">{it.label}</div>
                <div className="stat-over-bar"><i style={{ width: `${Math.min(100, w)}%` }} /></div>
                <div className="stat-over-v">{val ?? '—'}</div>
              </div>
            );
          })}
        </div>
      );
    }
    case 'bars': {
      const max = Math.max(1, ...desc.items.map((i) => Number(i.value) || 0));
      return (
        <div className="stat-bars">
          {desc.items.map((it, i) => (
            <div className="stat-bar-col" key={i}>
              <small>{it.value}</small>
              <i style={{ height: `${(Number(it.value) || 0) / max * 40}px` }} />
              <span>{it.label}</span>
            </div>
          ))}
        </div>
      );
    }
    default:
      return <div className="stat-tile-v">—</div>;
  }
}
